import {
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
} from '@mui/material';
import {
  tannerBlack,
  tannerGray,
  tannerSemantic,
  tannerWhite,
} from '../colors';

type MuiChip = {
  styleOverrides?: ComponentsOverrides<Theme>['MuiChip'];
  defaultProps?: ComponentsProps['MuiChip'];
  variants?: ComponentsVariants['MuiChip'];
};

export const MuiChip: MuiChip = {
  defaultProps: {
    variant: 'outlined',
    color: 'primary',
  },
  styleOverrides: {
    root: {
      marginRight: '8px',
      '&.Mui-focusVisible': {
        transition: 'background-color 300ms',
        outline: `3px solid ${tannerSemantic.info.color}`,
        outlineOffset: '3px',
      },
    },
    colorPrimary: {
      transition: 'all 300ms',
      color: tannerBlack,
      borderColor: tannerGray['300'],
      '&.MuiChip-clickable': {
        '&.MuiChip-outlined': {
          '&:hover': {
            transition:
              'background-color 300ms, border-color 300ms, box-shadow 300ms',
            boxShadow: `0 0 0 2px ${tannerGray['200']}`,
            borderColor: tannerGray['600'],
          },
        },
      },
      '& .MuiChip-deleteIcon': {
        color: tannerGray['600'],
        '&:hover, &:active': {
          color: tannerGray['700'],
          backgroundColor: tannerGray['200'],
        },
      },
    },
    colorError: {
      '& .MuiChip-deleteIcon': {
        color: tannerSemantic.error.color,
        '&:hover,&:active': {
          color: tannerSemantic.error.color,
          backgroundColor: tannerSemantic.error['100'],
        },
      },
      '&.MuiChip-clickable': {
        '&.MuiChip-outlined': {
          '&:hover': {
            boxShadow: `0 0 0 2px #FBC4C7`,
          },
        },
      },
      '&.MuiChip-filledError': {
        '& .MuiChip-deleteIcon': {
          '&:hover,&:active': {
            color: tannerWhite,
            backgroundColor: tannerSemantic.error['300'],
          },
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${tannerSemantic.info.color}`,
          outlineOffset: '3px',
          backgroundColor: '#D70E16',
        },
        '&.MuiChip-clickable:hover': {
          backgroundColor: '#B00007',
        },
      },
    },
    colorInfo: {
      color: tannerSemantic.info.color,
      borderColor: tannerSemantic.info.color,
      '& .MuiChip-deleteIcon': {
        color: tannerSemantic.info.color,
        '&:hover,&:active': {
          color: tannerSemantic.info.color,
          backgroundColor: tannerSemantic.info['100'],
        },
      },
      '&.MuiChip-clickable': {
        '&.MuiChip-outlined': {
          '&:hover': {
            boxShadow: `0 0 0 2px #BCD5F8`,
          },
        },
      },
      '&.MuiChip-filledInfo': {
        color: tannerWhite,
        backgroundColor: tannerSemantic.info.color,
        '&:focus-visible, &.Mui-focusVisible': {
          backgroundColor: tannerSemantic.info.color,
          outline: `3px solid ${tannerSemantic.info.color}`,
          outlineOffset: '3px',
        },
        '&.MuiChip-clickable': {
          '&:hover': {
            backgroundColor: '#0049AB',
          },
        },
        '& .MuiChip-deleteIcon': {
          '&:hover,&:active': {
            color: tannerWhite,
            backgroundColor: tannerSemantic.info['300'],
          },
        },
      },
    },
    colorSuccess: {
      '& .MuiChip-deleteIcon': {
        color: tannerSemantic.success.color,
        '&:hover,&:active': {
          color: tannerSemantic.success.color,
          backgroundColor: tannerSemantic.success['100'],
        },
      },
      '&.MuiChip-clickable': {
        '&.MuiChip-outlined': {
          '&:hover': {
            boxShadow: `0 0 0 2px #A2DDBE`,
          },
        },
      },
      '&.MuiChip-filledSuccess': {
        '& .MuiChip-deleteIcon': {
          '&:hover,&:active': {
            color: tannerWhite,
            backgroundColor: tannerSemantic.success['300'],
          },
        },
        '&.Mui-focusVisible': {
          backgroundColor: '#2E7D32',
          outline: `3px solid ${tannerSemantic.info.color}`,
          outlineOffset: '3px',
        },
        '&.MuiChip-clickable:hover': {
          backgroundColor: '#00511B',
        },
      },
    },
    filled: {
      '& .MuiChip-deleteIcon': {
        color: tannerWhite,
      },
      '&:hover': {
        boxShadow: `0 0 0 0`,
      },
      '&.MuiChip-deletable': {
        '& .MuiChip-label': {
          paddingRight: 11,
          paddingLeft: 12,
        },
      },
    },
    filledPrimary: {
      transition: 'all 300ms',
      color: tannerWhite,
      backgroundColor: tannerSemantic.info.color,
      '&.MuiChip-clickable': {
        '&.Mui-focusVisible': {
          transition: 'background-color 300ms',
          backgroundColor: tannerSemantic.info.color,
          outline: `3px solid ${tannerSemantic.info.color}`,
          outlineOffset: '3px',
        },
        '&:hover': {
          backgroundColor: '#0049AB',
          transition: 'background-color 300ms',
        },
      },
      '& .MuiChip-deleteIcon': {
        color: tannerWhite,
        '&:hover, &:active': {
          color: tannerWhite,
          backgroundColor: tannerSemantic.info['300'],
        },
      },
    },
    sizeMedium: {
      fontSize: '14px',
    },
    sizeSmall: {
      fontSize: '12px',
    },
  },
};
